/**
 * features/finance/PartyJournals.tsx
 * ------------------------------------------------------------------
 * DEBTOR AND CREDITOR JOURNALS — for the days an account is simply wrong.
 *
 * A receipt went to the wrong pupil. A balance came across twice.
 * Interest is due on an overdue account. A supplier allowed a settlement
 * discount. None of those is an invoice, a credit note or a payment, and
 * making a clerk fabricate one to correct an account is how a ledger
 * fills with documents describing things that never happened.
 *
 * THE CLERK NEVER PICKS A DEBIT OR A CREDIT. They choose whose account it
 * is and whether it should go up or down, in those words — "charge the
 * pupil more", "reduce what we owe". Which side of the double entry that
 * lands on depends on whether it is a debtor or a creditor, and working
 * that out is the system's job, not theirs.
 *
 * THE ENTRY IS SHOWN BEFORE IT IS POSTED. Not to make the clerk check the
 * debits — they should not have to — but so somebody who does read them
 * can, and so the screen is never a black box over the ledger.
 *
 * TWO THINGS IT REFUSES RATHER THAN WARNS ABOUT. It will not move money:
 * a bank contra would be a payment recorded with no receipt and no
 * reconciliation. And it will not be edited: a wrong journal is reversed
 * by an opposite one and both stay on the account.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import {
  Scale, Undo2, AlertTriangle, ArrowUp, ArrowDown, CheckCircle2,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { db } from '@/lib/firebase';
import { useGlData } from '@/features/finance/useGlData';
import {
  PARTY_JOURNAL_COLLECTION, PARTY_DIRECTION_LABEL, PARTY_REASONS,
  partyJournalLines, contraIsCashLike,
  type PartyJournal, type PartySide, type PartyDirection,
} from '@/lib/partyJournals';
import { postPartyJournal, reversePartyJournal } from '@/features/finance/partyJournalWrite';
import { SUPPLIER_COLLECTION, type Supplier } from '@/lib/suppliers';
import {
  PageHeader, SoftCard, PanelHeader, glassFieldClass, btnPrimary, btnGhost,
} from '@/features/finance/ui';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PartyJournals() {
  const { userData } = useAuth();
  const { students } = useData();
  const [fy] = useState(`FY${new Date().getFullYear()}`);
  const gl = useGlData(fy);

  const [rows, setRows] = useState<PartyJournal[] | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [side, setSide] = useState<PartySide>('debtor');
  const [outcome, setOutcome] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => onSnapshot(
    query(collection(db, PARTY_JOURNAL_COLLECTION)),
    (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as PartyJournal)),
    () => setRows([]),
  ), []);
  useEffect(() => onSnapshot(
    query(collection(db, SUPPLIER_COLLECTION)),
    (s) => setSuppliers(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Supplier)),
    () => setSuppliers([]),
  ), []);

  const actor = {
    uid: userData?.uid, name: userData?.displayName, email: userData?.email,
  };

  const systemKeyOf = (code: string) =>
    gl.accounts.find((a) => a.code === code)?.systemKey;

  const controlFor = (s: PartySide) =>
    gl.accounts.find((a) => a.systemKey === (s === 'debtor' ? 'AR_FEES' : 'AP_TRADE'))?.code ?? '';

  const mine = useMemo(
    () => (rows ?? []).filter((r) => r.side === side)
      .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? '')),
    [rows, side],
  );

  if (rows === null || gl.loading) {
    return <div className="p-6 flex justify-center"><Spinner /></div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <PageHeader
        icon={Scale} tone="navy"
        title="Debtor & Creditor Journals"
        subtitle="Correcting an account without inventing an invoice for it"
      />

      {outcome && (
        <SoftCard className={`mb-4 border-l-4 ${outcome.ok ? 'border-emerald-400' : 'border-red-400'}`}>
          <p className={`text-sm flex items-start gap-2 ${outcome.ok ? 'text-emerald-800 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
            {outcome.ok
              ? <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
              : <AlertTriangle size={15} className="mt-0.5 shrink-0" />}
            {outcome.text}
          </p>
        </SoftCard>
      )}

      <div className="flex gap-1 mb-4">
        {(['debtor', 'creditor'] as PartySide[]).map((s) => (
          <button key={s} type="button" onClick={() => setSide(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              side === s ? 'bg-primary text-white'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}>
            {s === 'debtor' ? 'Pupil accounts' : 'Supplier accounts'}
          </button>
        ))}
      </div>

      <Composer
        key={side}
        side={side}
        control={controlFor(side)}
        accounts={gl.accounts}
        systemKeyOf={systemKeyOf}
        parties={side === 'debtor'
          ? students.map((s) => ({ id: s.id, name: s.fullName }))
          : suppliers.map((s) => ({ id: s.id, name: s.name }))}
        actor={actor}
        onDone={setOutcome}
      />

      <SoftCard pad="p-0" className="mt-5">
        <PanelHeader icon={Scale} tone="navy"
          title={side === 'debtor' ? 'Pupil journals' : 'Supplier journals'}
          subtitle="Never edited — a wrong one is reversed and both stay on the account" />
        {mine.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">Nothing posted yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                  <th className="py-2 pl-4 pr-2 font-semibold">Reference</th>
                  <th className="px-2 font-semibold">Account</th>
                  <th className="px-2 font-semibold">Why</th>
                  <th className="px-2 font-semibold text-right">Effect</th>
                  <th className="px-2 pr-4 w-8" />
                </tr>
              </thead>
              <tbody>
                {mine.map((r) => {
                  const dead = Boolean(r.reversedBy || r.reversesId);
                  const up = r.direction === 'charge';
                  return (
                    <tr key={r.id} className={`border-b border-gray-50 dark:border-gray-800 ${dead ? 'opacity-50' : ''}`}>
                      <td className="py-2 pl-4 pr-2">
                        <span className="font-mono text-xs text-gray-600 dark:text-gray-300">
                          {r.journalRef}
                        </span>
                        <span className="block text-[11px] text-gray-400">{r.date}</span>
                      </td>
                      <td className="px-2 text-gray-800 dark:text-gray-100">{r.partyName}</td>
                      <td className="px-2 text-gray-600 dark:text-gray-300 truncate max-w-[280px]">
                        {r.reason}
                      </td>
                      <td className={`px-2 text-right tabular-nums font-medium ${
                        up ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-400'
                      }`}>
                        {up ? <ArrowUp size={12} className="inline -mt-0.5" />
                          : <ArrowDown size={12} className="inline -mt-0.5" />}
                        {' '}{money(r.amount)}
                      </td>
                      <td className="px-2 pr-4">
                        {!dead && (
                          <button type="button" title="Reverse this journal"
                            className="text-gray-400 hover:text-red-600"
                            onClick={async () => {
                              if (!window.confirm(`Reverse ${r.journalRef}? An opposite journal is posted and both stay on the account.`)) return;
                              try {
                                await reversePartyJournal(
                                  r,
                                  { control: controlFor(r.side), contra: r.contraAccountCode },
                                  systemKeyOf, actor,
                                );
                                setOutcome({ ok: true, text: `${r.journalRef} reversed.` });
                              } catch (e) {
                                setOutcome({ ok: false, text: (e as Error).message });
                              }
                            }}>
                            <Undo2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SoftCard>
    </div>
  );
}

/* ------------------------------- composer ------------------------------- */

function Composer({
  side, control, accounts, systemKeyOf, parties, actor, onDone,
}: {
  side: PartySide;
  control: string;
  accounts: ReturnType<typeof useGlData>['accounts'];
  systemKeyOf: (code: string) => string | undefined;
  parties: { id: string; name: string }[];
  actor: { uid?: string; name?: string; email?: string };
  onDone: (o: { ok: boolean; text: string }) => void;
}) {
  const [partyId, setPartyId] = useState('');
  const [direction, setDirection] = useState<PartyDirection>('charge');
  const [amount, setAmount] = useState('');
  const [contra, setContra] = useState('');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const party = parties.find((p) => p.id === partyId);
  const cashContra = contra ? contraIsCashLike(contra, systemKeyOf) : false;

  /* The contra list excludes the two control accounts — a journal from
     receivables to receivables is not a correction, it is a no-op that
     balances. */
  const contras = useMemo(
    () => accounts
      .filter((a) => a.systemKey !== 'AR_FEES' && a.systemKey !== 'AP_TRADE')
      .sort((a, b) => a.code.localeCompare(b.code)),
    [accounts],
  );

  /* Shown before posting. Not so the clerk checks the debits — they should
     not have to — but so the screen is never a black box over the ledger. */
  const preview = useMemo(() => {
    if (!party || !contra || !control || !(Number(amount) > 0)) return null;
    try {
      return partyJournalLines({
        side, direction, amount: Number(amount),
        partyName: party.name, reason: reason || '—', journalRef: '(next)',
      }, { control, contra });
    } catch {
      return null;
    }
  }, [side, direction, amount, party, reason, control, contra]);

  async function post() {
    if (!party) return;
    setBusy(true);
    try {
      const j = await postPartyJournal({
        side, direction, partyId, partyName: party.name,
        contraAccountCode: contra, amount: Number(amount),
        reason, date, accounts: { control, contra }, systemKeyOf, actor,
      });
      onDone({ ok: true, text: `${j.journalRef} posted against ${j.partyName}.` });
      setAmount(''); setReason(''); setPartyId('');
    } catch (e) {
      onDone({ ok: false, text: (e as Error).message });
    }
    setBusy(false);
  }

  if (!control) {
    return (
      <SoftCard className="border-l-4 border-amber-400">
        <p className="text-sm text-amber-800 dark:text-amber-300">
          No {side === 'debtor' ? 'AR_FEES' : 'AP_TRADE'} control account is mapped in the
          chart of accounts, so there is nothing for a journal to move against.
        </p>
      </SoftCard>
    );
  }

  return (
    <SoftCard pad="p-0">
      <PanelHeader icon={Scale} tone="navy" title="Correct an account"
        subtitle="Choose whose account, and which way it should move — not a debit or a credit" />

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">
              {side === 'debtor' ? 'Pupil' : 'Supplier'}
            </span>
            <select value={partyId} onChange={(e) => setPartyId(e.target.value)}
              className={glassFieldClass('mt-1')}>
              <option value="">— choose —</option>
              {[...parties].sort((a, b) => a.name.localeCompare(b.name)).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>

          {/* The whole point: this is in the clerk's words, not the ledger's. */}
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">
              Which way
            </span>
            <select value={direction}
              onChange={(e) => setDirection(e.target.value as PartyDirection)}
              className={glassFieldClass('mt-1')}>
              <option value="charge">{PARTY_DIRECTION_LABEL[side].charge}</option>
              <option value="credit">{PARTY_DIRECTION_LABEL[side].credit}</option>
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">Amount</span>
            <input type="number" step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={glassFieldClass('mt-1 text-right')} />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className={glassFieldClass('mt-1')} />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">
              Where the other side goes
            </span>
            <select value={contra} onChange={(e) => setContra(e.target.value)}
              className={glassFieldClass('mt-1')}>
              <option value="">— choose —</option>
              {contras.map((a) => (
                <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
              ))}
            </select>
          </label>
        </div>

        {cashContra && (
          <p className="text-[13px] text-red-600">
            That is a bank or cash account. A journal here cannot move money — record a
            receipt or a payment instead, so it is numbered and reconciled.
          </p>
        )}

        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-gray-400">Why</span>
          <input list={`reasons-${side}`} value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={glassFieldClass('mt-1')}
            placeholder="Pick one, or write your own" />
          <datalist id={`reasons-${side}`}>
            {PARTY_REASONS[side].map((r) => <option key={r} value={r} />)}
          </datalist>
        </label>

        {preview && (
          <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">
              What will be posted
            </p>
            {preview.map((l, i) => (
              <p key={i} className="text-[13px] tabular-nums text-gray-700 dark:text-gray-200">
                {accounts.find((a) => a.code === l.accountCode)?.name ?? l.accountCode}
                <span className="text-gray-400"> · </span>
                {l.dr ? `Dr ${money(l.dr)}` : `Cr ${money(l.cr)}`}
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-100 dark:border-gray-700">
        <button type="button" className={btnGhost}
          onClick={() => { setAmount(''); setReason(''); setPartyId(''); setContra(''); }}>
          Clear
        </button>
        <button type="button" className={btnPrimary} onClick={post}
          disabled={busy || cashContra || !party || !contra || !(Number(amount) > 0) || !reason.trim()}>
          {busy ? 'Posting…' : 'Post the journal'}
        </button>
      </div>
    </SoftCard>
  );
}
